import { useCallback } from 'react';
import { PhotoIcon, TrashIcon } from '@heroicons/react/24/outline';
import type { CellDragPayload } from './gridCellDnd';
import type { CellData } from './imageGridShared';
import { useAspectRatioMedia, useCaptionDraft } from './useCellMedia';
import { useCellDragAndDrop } from './useCellDragAndDrop';
import { ColHandle } from './ColHandle';

interface CellProps {
    cell: CellData;
    cellIndex: number;
    colIndex: number;
    colWidths: number[];
    isLastCol: boolean;
    editable: boolean;
    showCaptions: boolean;
    onRemoved: (cellIndex: number) => void;
    onResize: (updated: number[]) => void;
    onPick: (cellIndex: number) => void;
    onDropFile: (cellIndex: number, file: File) => void;
    onDragStartCell: (cellIndex: number) => string | null;
    onMoveCell: (targetCellIndex: number, payload: CellDragPayload) => void;
    onCaptionChange: (cellIndex: number, caption: string) => void;
}

/** Read-only rendering: just the image (or nothing for an empty cell). No
 * drag, click-to-pick, trash button, resize handle, or empty-cell placeholder.
 * Takes the parent's aspect-ratio ref/callbacks rather than calling the hook
 * itself, since GridCell must call all hooks unconditionally (rules-of-hooks)
 * before branching on `editable`. */
function ReadOnlyCell({
    cell,
    showCaptions,
    mediaRef,
    imageRef,
    handleImageLoad
}: Pick<CellProps, 'cell' | 'showCaptions'> &
    Pick<ReturnType<typeof useAspectRatioMedia>, 'mediaRef' | 'imageRef' | 'handleImageLoad'>) {
    const hasCaption = showCaptions && !!cell.src && cell.caption.trim().length > 0;

    return (
        <div
            className={`image-grid-cell${cell.src ? ' is-filled' : ''}`}
            style={{ position: 'relative', overflow: 'hidden', height: '100%' }}
        >
            {cell.src &&
                (showCaptions ? (
                    <div
                        className={`image-grid-cell-media${hasCaption ? ' has-label' : ''}`}
                        ref={mediaRef}
                    >
                        <img
                            src={cell.src}
                            alt={cell.caption}
                            draggable={false}
                            ref={imageRef}
                            onLoad={handleImageLoad}
                        />
                        {hasCaption && (
                            <div className="image-grid-cell-caption-text">{cell.caption}</div>
                        )}
                    </div>
                ) : (
                    <img src={cell.src} alt="" draggable={false} />
                ))}
        </div>
    );
}

export function GridCell({
    cell,
    cellIndex,
    colIndex,
    colWidths,
    isLastCol,
    editable,
    showCaptions,
    onRemoved,
    onResize,
    onPick,
    onDropFile,
    onDragStartCell,
    onMoveCell,
    onCaptionChange
}: CellProps) {
    // Hooks run unconditionally (rules-of-hooks) even on the read-only branch
    // below, where their callbacks simply go unused.
    const { mediaRef, imageRef, handleImageLoad } = useAspectRatioMedia();
    const { captionDraft, setCaptionDraft, commitCaption } = useCaptionDraft(
        cell.caption,
        cellIndex,
        onCaptionChange
    );
    const {
        isDragOver,
        handleCellDragStart,
        handleDragOver,
        handleDragEnter,
        handleDragLeave,
        handleDrop
    } = useCellDragAndDrop({ cellIndex, onDragStartCell, onMoveCell, onDropFile });

    const handleClick = useCallback(() => {
        if (!cell.src) onPick(cellIndex);
    }, [cell.src, cellIndex, onPick]);

    const handleRemove = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            onRemoved(cellIndex);
        },
        [cellIndex, onRemoved]
    );

    if (!editable) {
        return (
            <ReadOnlyCell
                cell={cell}
                showCaptions={showCaptions}
                mediaRef={mediaRef}
                imageRef={imageRef}
                handleImageLoad={handleImageLoad}
            />
        );
    }

    const captioned = showCaptions && !!cell.src;
    const trashButton = (
        <button
            type="button"
            className="cell-trash-btn"
            onClick={handleRemove}
            title="Remove image"
            aria-label="Remove image"
        >
            <TrashIcon style={{ width: 12, height: 12 }} />
        </button>
    );

    return (
        <div
            className={`image-grid-cell${cell.src ? ' is-filled' : ''}${isDragOver ? ' is-drag-over' : ''}`}
            style={{
                position: 'relative',
                overflow: 'hidden',
                height: '100%',
                cursor: cell.src ? (captioned ? 'default' : 'grab') : 'pointer'
            }}
            draggable={!!cell.src && !captioned}
            onDragStart={captioned ? undefined : handleCellDragStart}
            onClick={handleClick}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            {cell.src ? (
                captioned ? (
                    <div
                        className="image-grid-cell-media has-label"
                        ref={mediaRef}
                        draggable
                        onDragStart={handleCellDragStart}
                        style={{ cursor: 'grab' }}
                    >
                        <img
                            src={cell.src}
                            alt={cell.caption}
                            draggable={false}
                            ref={imageRef}
                            onLoad={handleImageLoad}
                        />
                        {trashButton}
                        <input
                            className="image-grid-cell-caption-input"
                            type="text"
                            value={captionDraft}
                            placeholder="Add a label…"
                            draggable={false}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                            // The media box is the drag source for cell moves; a
                            // drag started on the label must not move the cell.
                            onDragStart={(e) => e.stopPropagation()}
                            onChange={(e) => setCaptionDraft(e.target.value)}
                            onBlur={commitCaption}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    commitCaption();
                                    (e.target as HTMLInputElement).blur();
                                }
                            }}
                        />
                    </div>
                ) : (
                    <>
                        <img src={cell.src} alt="" draggable={false} />
                        {trashButton}
                    </>
                )
            ) : (
                <div className="image-grid-empty-cell">
                    <PhotoIcon width={28} height={28} className="image-grid-empty-icon" />
                    <span className="image-grid-empty-label">
                        {isDragOver ? 'Drop image' : 'Click or drop image'}
                    </span>
                </div>
            )}
            {!isLastCol && (
                <ColHandle colIndex={colIndex} colWidths={colWidths} onResize={onResize} />
            )}
        </div>
    );
}
